import { defineStore } from 'pinia';
import { ref } from 'vue';
import { autoAuctionApiSdk } from '../sdk/autoAuctionClient.js';
import { idOf } from '../sdk/identity.js';
import { useGeolocation } from '../adapters/ports.js';
import { useVehicleSearchStore } from './vehicle-search.store.js';
import vehicleCacheStore from './vehicle-cache.store.js';
import { likedStore } from './liked.store.js';
import { auctionHousesStore } from './auction-houses.store.js';

// Explore's feed is the CATALOG — vibe-server's `items` collection, read
// through the server-side search (ItemsSearchService) by one of its two doors:
//
//   facets — /api/items/subsetByQuery, the shape a filter UI or a saved search
//            builds (MAKE[], YEAR "min-max"), folded with the free-text term
//            and the category chip by activeQuery(). The query may also carry
//            an `fql` string — the part of an AI search no panel control can
//            hold — which the server compiles and ANDs with the facets. It is
//            just another key here: it rides in lastQuery and out again, and
//            this store never reads or writes its contents.
//   filter — /api/items/searchByFilter, the Extended-JSON Mongo filter the AI
//            assistant's FQL compiled to, committed whole by
//            searchWithMongoFilter()
//
// A committed mongo filter is what picks the door, for the first page and every
// page after it. There is no translating one shape into the other: a Mongo
// filter says things no facet key can, so an AI search that arrived as facets
// would be a different search from the one the dealer asked for.
//
// The catalog holds every item, whether or not a sale is scheduled for it. That
// is deliberate and is why Explore reads it rather than the event runtime: an
// item with no sale yet is a normal, browsable, savable state, and a feed
// sourced from the runtime could only ever show cars already on the block.
//
// Sale membership is a SEPARATE, non-blocking lookup (see the sale index) — the
// feed itself has exactly one hard dependency, this search endpoint. A runtime
// outage must never empty Explore.
//
// Identity: an item's `_id` is the id the runtime stores its lot under when the
// sale is created (the runList carries real item _ids and the importer keys on
// them), so a card saved from this feed is the same vehicle the Bid Stack and
// Live Bidding key on. No id translation anywhere.
//
// Search/facets/pagination are all server-side; the only client-side predicates
// are radius and on-my-list, which the server can't express (no geo on items, no
// saved on the server).

export default defineStore('vehicleResultsStore', () => {
    const vehicleSearchStore = useVehicleSearchStore();
    const vehicleCache = vehicleCacheStore();
    const auctionHouses = auctionHousesStore();

    const PAGE_SIZE = 50;

    const searchables = ref({});
    const results = ref([]);
    const initialLoading = ref(true);
    // True once a search has SUCCESSFULLY resolved, so the page can tell
    // "loaded, zero matches" from "never loaded". Also set on failure — a failed
    // fetch must not leave the feed pinned in a permanent loading state; `error`
    // is what distinguishes the two.
    const loaded = ref(false);
    // Last fetch error, or null. Lets Explore render "couldn't load" instead of
    // the misleading "No matches yet." when the API is down.
    const error = ref(null);
    const loadingMore = ref(false);
    const page = ref(1);
    const hasMore = ref(false);
    const totalPages = ref(0);
    const totalCount = ref(0);

    // Last structured query committed via searchWith() (the SearchDrawer /
    // saved-search shape: MAKE/MODEL/SERIES arrays, YEAR "min-max", etc.).
    // Pagination reuses it so loadMore stays consistent with the active filters.
    const lastQuery = ref(null);
    // The Mongo filter committed via searchWithMongoFilter(), or null when the
    // feed is on the facet door. Non-null is what makes every fetch — first page
    // and each page after — go to /items/searchByFilter instead.
    const mongoFilter = ref(null);
    // The FQL the committed mongo filter compiled from, so Explore can name the
    // AI search on screen. Empty when the feed is on the facet door.
    const fql = ref('');
    // Free-text term from the Explore search box / AI handoff, folded on as `q`.
    const searchText = ref('');
    // Active Explore category chip ('all' | 'luxury' | 'trucks' | ...). The
    // server turns this into the right body/make/fuel constraint.
    const category = ref('all');
    // Scroll offset of the Explore feed, preserved across a detail round-trip.
    const scrollTop = ref(0);

    // "Near me" radius. Items carry only an AUCTIONID string (no GeoJSON point),
    // so distance is resolved client-side from AUCTIONID -> auction coords. The
    // trade-off: totalCount stays the server's pre-distance count, so the header
    // can read slightly high while a radius is active.
    const radiusBucket = ref(null);
    // Client-side result order (Sale time · Distance · MMR · Newest). Lived on
    // ResultsPage chrome; Filters owns it after Josh #253 chrome trim.
    const sortBy = ref('saleTime');
    // Where the dealer is, or null while nobody has said.
    //
    // It used to start at a hardcoded Sacramento constant, which read as a
    // measurement rather than as the absence of one — every listing had a
    // plausible distance from a place the dealer had never been. Null is the
    // honest starting value, and it degrades the way an unknown auction house
    // does: an un-placeable listing passes the filter rather than being hidden.
    const userOrigin = ref(null);
    // "On my list" — saved membership lives only in the browser.
    const onListOnly = ref(false);

    // Snapshot of the Filter drawer's committed UI selections, so reopening the
    // drawer reflects the active search instead of resetting to defaults.
    const filterState = ref(null);

    // An auction we can't place passes through rather than being hidden: an
    // unknown AUCTIONID under an active radius should never silently empty the
    // feed. (The pre-8549b0c version's comment claimed this but its code did the
    // opposite — `dist <= limit` drops Infinity.)
    const withinRadius = (docs) => {
        const limit = radiusBucket.value;
        if (limit == null) return docs;
        // Nowhere to measure from is the same answer as nowhere to measure to.
        if (userOrigin.value == null) return docs;
        return docs.filter((doc) => {
            const dist = auctionHouses.distanceFrom(doc?.AUCTIONID, userOrigin.value);
            return dist === Infinity ? true : dist <= limit;
        });
    };

    const onList = (docs) => {
        if (!onListOnly.value) return docs;
        const saved = likedStore();
        return docs.filter((doc) => saved.isLiked(idOf(doc) ?? doc?.VIN));
    };

    const applyClientFilters = (docs) => onList(withinRadius(docs ?? []));

    // Seed the vehicle cache from each fetched page so a tapped result paints
    // instantly on the detail page instead of flashing a spinner while it
    // refetches the doc we already hold.
    const primeCache = (docs) => {
        for (const doc of docs ?? []) {
            const id = idOf(doc);
            if (id) vehicleCache.set(String(id), doc);
        }
    };

    // Resolve where the dealer is, through the host's geolocation port —
    // best-effort and permission-gated, and answering null rather than
    // rejecting when it cannot say.
    //
    // The house roster is pulled alongside it, because a distance needs both
    // ends: without it every AUCTIONID is un-placeable. Both failures are safe
    // by construction — an un-placeable listing passes the radius filter — so
    // this is awaited for correctness of the filter, never as a gate on the
    // feed.
    const ensureOrigin = async () => {
        const [origin] = await Promise.all([
            useGeolocation().currentPosition(),
            auctionHouses.ensureLoaded()
        ]);
        userOrigin.value = origin ?? null;
    };

    // The committed query plus the chrome the page owns. Every other key of
    // lastQuery — `fql` included — passes through untouched.
    const activeQuery = () => {
        const query = { ...(lastQuery.value ?? vehicleSearchStore.getSearchBySelections ?? {}) };
        const term = searchText.value.trim();
        if (term) query.q = term;
        if (category.value && category.value !== 'all') query.category = category.value;
        return query;
    };

    // The one place a page of results comes from, whichever door is active.
    const fetchResults = async (startingIndex) => {
        if (mongoFilter.value) {
            return autoAuctionApiSdk.getAssetsByFilter(
                mongoFilter.value,
                { startingIndex, size: PAGE_SIZE }
            );
        }
        return autoAuctionApiSdk.getAssetsSubsetByQuery(
            activeQuery(),
            { startingIndex, size: PAGE_SIZE }
        );
    };

    // Drop back to the facet door. Every facet entry point calls this, so a
    // committed AI search never survives the next drawer commit, text search or
    // category chip and silently keep narrowing the feed.
    const clearMongoFilter = () => {
        mongoFilter.value = null;
        fql.value = '';
    };

    // Monotonic token guarding previewCount against out-of-order responses: a
    // slow earlier request must not clobber a newer count as the user keeps
    // toggling filters.
    let previewSeq = 0;

    // The single fetch path for every "replace the feed" entry point (initial
    // load, drawer commit, text search, category chip, radius change). They
    // differ only in what they set before calling — not in how they fetch.
    //
    // resetScroll: false only for the initial search(), which must preserve the
    // offset restored on a detail round-trip.
    const runSearch = async ({ resetScroll = true } = {}) => {
        initialLoading.value = true;
        if (resetScroll) scrollTop.value = 0;
        page.value = 1;

        try {
            const result = await fetchResults(0);

            searchables.value = result.searchables;
            results.value = applyClientFilters(result.documents);
            primeCache(result.documents);
            totalCount.value = result.meta?.totalCount || 0;
            totalPages.value = Math.ceil(totalCount.value / PAGE_SIZE);
            hasMore.value = page.value < totalPages.value;
            error.value = null;
            loaded.value = true;
        } catch (err) {
            // Mark loaded so the page leaves the spinner and renders the error
            // state; `error` is what tells it apart from a genuine zero-match.
            // Re-throw so the caller's contextual .catch() logging still fires.
            loaded.value = true;
            error.value = err;
            throw err;
        } finally {
            // Always clear the spinner — a thrown fetch must not leave the feed
            // stuck on the loading state forever.
            initialLoading.value = false;
        }
    };

    return {
        initialLoading,
        loaded,
        error,
        loadingMore,
        results,
        searchables,
        page,
        hasMore,
        totalPages,
        totalCount,
        lastQuery,
        mongoFilter,
        fql,
        searchText,
        category,
        scrollTop,
        radiusBucket,
        sortBy,
        userOrigin,
        onListOnly,
        filterState,

        // Set the client-side "On my list" flag WITHOUT fetching. The drawer
        // calls this just before searchWith() so the committed search applies
        // the predicate in the same pass.
        prepareOnList: (next) => {
            onListOnly.value = !!next;
        },

        // Persist the Filter drawer's committed UI selections so a later reopen
        // restores them. Pass null to clear (e.g. on Reset).
        setFilterState: (next) => {
            filterState.value = next ?? null;
        },

        // Find an already-loaded doc by its normalized id — the detail page
        // renders instantly from what the feed already fetched.
        findById: (id) => results.value.find((d) => idOf(d) === id) ?? null,

        fetchPage: async (pageNumber = 1) => {
            return await fetchResults(Math.max(0, (pageNumber - 1) * PAGE_SIZE));
        },

        // Initial load. Preserves scrollTop so a detail round-trip can restore
        // the feed's offset, and keeps whichever door is committed — returning
        // to Explore must re-run the search the dealer is looking at.
        search: async () => runSearch({ resetScroll: false }),

        // Commit a structured query (from the drawer or a saved search).
        searchWith: async (query) => {
            clearMongoFilter();
            lastQuery.value = { ...(query || {}) };
            return runSearch();
        },

        // Commit the Mongo filter an AI search produced, and page through it by
        // the filter door from here. `fqlLabel` is the FQL it compiled from, so
        // Explore can show the dealer what is narrowing the feed.
        //
        // The facet state goes with it: no snapshot of a drawer can stand for a
        // Mongo filter, and a stale one would read as the active search.
        searchWithMongoFilter: async (filter, { fqlLabel = '' } = {}) => {
            if (!filter || typeof filter !== 'object') return false;
            mongoFilter.value = filter;
            fql.value = typeof fqlLabel === 'string' ? fqlLabel : '';
            filterState.value = null;
            lastQuery.value = null;
            searchText.value = '';
            category.value = 'all';
            await runSearch();
            return true;
        },

        // Set the free-text term and refresh (Explore search box / chip clear).
        searchByText: async (term) => {
            clearMongoFilter();
            searchText.value = typeof term === 'string' ? term : '';
            return runSearch();
        },

        // Set the active category chip and refresh.
        setCategory: async (next) => {
            clearMongoFilter();
            category.value = typeof next === 'string' ? next : 'all';
            return runSearch();
        },

        // Lightweight match-count preview for a facet query, before it is
        // committed. Fetches a single document (size: 1 — the endpoint has no
        // count-only mode) purely to read meta.totalCount, WITHOUT touching
        // results/lastQuery so the live feed stays put. Returns the count, or
        // null if a newer preview superseded this one.
        previewCount: async (query) => {
            const seq = (previewSeq += 1);
            const result = await autoAuctionApiSdk.getAssetsSubsetByQuery(
                { ...(query || {}) },
                { startingIndex: 0, size: 1 }
            );
            if (seq !== previewSeq) return null;
            const count = result.meta?.totalCount || 0;
            totalCount.value = count;
            return count;
        },

        // The catalog's actual values for ONE field under a Mongo filter — the
        // `searchables` the search endpoint already answers with, read through a
        // one-document fetch because the endpoint has no values-only mode.
        //
        // This is how a contains-text FQL clause becomes exact facet chips. The
        // clause runs as a case-insensitive $regex; the distinct catalog values
        // it matches ARE the chips, so the panel shows what the search runs
        // rather than the words the dealer or the assistant typed.
        //
        // Deliberately touches nothing else: not results, not totalCount, not
        // the loading flags. It is a lookup BESIDE the feed, not a search of it,
        // and a spinner or an overwritten count would misreport the feed.
        //
        // A missing field answers []. A failed fetch throws, because a caller
        // that cannot resolve a term must keep it as an AI term rather than
        // commit a chip the search does not run.
        searchablesForFilter: async (filter, field) => {
            const result = await autoAuctionApiSdk.getAssetsByFilter(
                filter || {},
                { startingIndex: 0, size: 1 }
            );
            const values = result?.searchables?.[field];
            if (!Array.isArray(values)) return [];
            // An entry is either the value itself or { value, count }.
            return values
                .map((entry) => (entry && typeof entry === 'object' ? entry.value : entry))
                .filter((value) => typeof value === 'string' && value !== '');
        },

        // Append the next page. Drives Explore's infinite scroll.
        loadMore: async () => {
            if (!hasMore.value || loadingMore.value) return false;

            loadingMore.value = true;
            page.value += 1;

            try {
                const result = await fetchResults((page.value - 1) * PAGE_SIZE);

                results.value = [...results.value, ...applyClientFilters(result.documents)];
                primeCache(result.documents);
                totalCount.value = result.meta?.totalCount || totalCount.value;
                totalPages.value = Math.ceil(totalCount.value / PAGE_SIZE);
                hasMore.value = page.value < totalPages.value;

                return true;
            } catch (err) {
                // Roll the page back so a retry re-fetches the page that failed
                // instead of skipping it and leaving a gap in the feed.
                page.value -= 1;
                throw err;
            } finally {
                // Always release the guard — a thrown fetch must not wedge
                // loadingMore on and block every future loadMore() call.
                loadingMore.value = false;
            }
        },

        // Sync the radius bucket (and resolve location) WITHOUT fetching. The
        // drawer calls this just before searchWith() so the committed search
        // applies the distance predicate in a single fetch rather than two.
        prepareRadius: async (bucket) => {
            radiusBucket.value = bucket == null ? null : Number(bucket);
            if (radiusBucket.value != null) await ensureOrigin();
        },

        setSortBy: (next) => {
            const allowed = new Set(['saleTime', 'distance', 'mmr', 'newest']);
            sortBy.value = allowed.has(next) ? next : 'saleTime';
        },

        // Set the "Near me" radius bucket (null == no limit) and re-run the
        // active search. For a real limit, resolve the user's location first so
        // the first filtered page is measured from where they actually are
        // rather than the fallback origin.
        setRadius: async (bucket) => {
            radiusBucket.value = bucket == null ? null : Number(bucket);
            if (radiusBucket.value != null) await ensureOrigin();
            return runSearch();
        }
    };
});
